import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, ChevronRight, ChevronDown, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Yataş Grubu · Davranış Segmentasyonu (/yatas/davranis-segmentasyonu)
 * Niyet (ne için geldi) -> giriş (nereye girdi) -> davranış (sonra ne yaptı).
 * Kaynaktan bağımsız kümeler; çizgili huni YOK: kartlar, tablolar, yatay çubuklar.
 *
 * Backend: GET /api/behavior-segments?days=7|28|90[&refresh=1]  (X-Brand-ID, cookie oturumu).
 * Oranlar 0-1; per100 = o kümenin her 100 oturumunda o sayfaya geçiş sayısı (%100'ü aşabilir).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', warn: '#B45309', warnSoft: '#FFF7ED',
  green: '#1baf7a', orange: '#eb6834', amber: '#eda100', purple: '#9b59d0'
};
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const nf = (n, d = 0) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d });
const tl = (n) => `₺${nf(n)}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${nf(v / 1e6, 2)} Mn`;
  if (Math.abs(v) >= 1e4) return `₺${nf(v / 1e3)} B`;
  return tl(v);
};
const pct = (x, d) => {
  const v = (x || 0) * 100;
  const dd = d != null ? d : (v > 0 && v < 1 ? 2 : 1);
  return `%${nf(v, dd)}`;
};
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const gunEtiket = (s) => (s ? `${Number(s.slice(8, 10))} ${AY[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}` : '');

const BASIS_LABEL = {
  search_term: 'Google Ads arama terimi', campaign: 'Kampanya adı',
  gsc_estimate: 'Search Console (tahmini)', landing: 'Giriş sayfası adı'
};
const BASIS_COLOR = { search_term: C.accent, campaign: C.green, gsc_estimate: C.amber, landing: '#C4C9D4' };
const KIND_LABEL = { brand: 'Marka', brand_category: 'Marka + kategori', generic: 'Genel', competitor: 'Rakip' };

const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
const th = { textAlign: 'right', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', background: '#FAFBFF' };
const td = { textAlign: 'right', padding: '9px 12px', fontSize: 13, color: C.ink2, borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap' };
const tdL = { ...td, textAlign: 'left' };
const thL = { ...th, textAlign: 'left' };

function Secici({ secenekler, deger, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: C.hair, borderRadius: 10, padding: 3 }}>
      {secenekler.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} style={{
          border: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 13, padding: '6px 12px', borderRadius: 8,
          background: deger === k ? C.card : 'transparent', color: deger === k ? C.ink : C.muted,
          fontWeight: deger === k ? 600 : 500, boxShadow: deger === k ? '0 1px 2px rgba(16,24,40,.08)' : 'none'
        }}>{label}</button>
      ))}
    </div>
  );
}

function Bolum({ baslik, alt, sag, children, style, id }) {
  return (
    <section id={id} style={{ ...kart, padding: 20, marginTop: 20, ...style }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink }}>{baslik}</h2>
          {alt && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, maxWidth: 760 }}>{alt}</div>}
        </div>
        {sag}
      </div>
      {children}
    </section>
  );
}

function Kpi({ etiket, deger, alt }) {
  return (
    <div style={{ ...kart, padding: '16px 18px' }}>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>{etiket}</div>
      <div style={{ fontSize: 25, fontWeight: 700, color: C.ink, marginTop: 6, letterSpacing: -0.5 }}>{deger}</div>
      {alt && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{alt}</div>}
    </div>
  );
}

function Etiket({ children, renk = 'amber' }) {
  const s = renk === 'amber' ? { background: C.warnSoft, color: C.warn } : { background: C.accentSoft, color: C.accent };
  return <span style={{ ...s, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 6, marginLeft: 6, whiteSpace: 'nowrap' }}>{children}</span>;
}

/**
 * Öne çıkan bulgular (18 Eki 2026, Yataş notu): sayfadaki veriden Claude'un seçtiği 4-5 bulgu.
 * Backend: GET /api/behavior-segments/insights — veri yeniden hesaplanmadıkça önbellekten gelir.
 * Niyete bağlı bulguya tıklayınca o niyetin ayrıntısı açılır.
 */
const KIND_STYLE = {
  risk: { label: 'Risk', color: C.orange, soft: '#FDF0EA' },
  firsat: { label: 'Fırsat', color: C.green, soft: '#E8F7F1' },
  tespit: { label: 'Tespit', color: C.accent, soft: C.accentSoft },
};
const METRIC_LABEL = { purchaseRate: 'satın alma oranı', atcRate: 'sepete ekleme oranı', bounceRate: 'etkileşimsiz çıkış', revenueShare: 'ciro payı' };

/** Bulgunun ana sayısını sayfa verisinden çözer (AI metnine değil): segment değeri vs site ortalaması. */
function bulguOlcusu(b, veri) {
  if (!b.metric || b.metric === 'none' || !veri) return null;
  const it = b.intentKey ? (veri.intents || []).find((i) => i.key === b.intentKey) : null;
  const ch = b.channel ? (veri.channels || []).find((c) => c.channel === b.channel) : null;
  let seg = null; let ad = '';
  if (it && b.channel) {
    seg = (it.byChannel || []).find((c) => c.channel === b.channel);
    ad = seg ? `${it.label} · ${seg.label}` : '';
  } else if (it) { seg = it; ad = it.label; } else if (ch) { seg = ch; ad = ch.label; }
  if (!seg) return null;
  const t = veri.totals;
  if (b.metric === 'revenueShare') {
    if (!t.revenue) return null;
    return { seg, ad, deger: seg.revenue / t.revenue, taban: seg.sessions / t.sessions, etiket: 'ciro payı', tabanEtiket: 'oturum payı' };
  }
  if (t[b.metric] == null || seg[b.metric] == null) return null;
  return { seg, ad, deger: seg[b.metric], taban: t[b.metric], etiket: METRIC_LABEL[b.metric], tabanEtiket: 'site ortalaması' };
}

function KiyasCubugu({ o, renk, h }) {
  const maks = Math.max(o.deger, o.taban) || 1;
  const satir = (etiket, v, r, kalin) => (
    <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr 52px', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <span style={{ color: kalin ? C.ink2 : C.muted, fontWeight: kalin ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{etiket}</span>
      <div style={{ height: h, background: C.hair, borderRadius: h }}>
        <div style={{ width: `${Math.max(2, (v / maks) * 100)}%`, height: '100%', background: r, borderRadius: h }} />
      </div>
      <span style={{ textAlign: 'right', color: kalin ? C.ink : C.muted, fontWeight: kalin ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>{pct(v)}</span>
    </div>
  );
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {satir(o.etiket === 'ciro payı' ? 'Ciro payı' : 'Bu segment', o.deger, renk, true)}
      {satir(o.tabanEtiket === 'oturum payı' ? 'Oturum payı' : 'Site ort.', o.taban, '#C4C9D4', false)}
    </div>
  );
}

/** Büyük kartın altı: segmentin dört temel ölçüsü, site ortalamasıyla. */
function SegmentOzeti({ seg, t }) {
  const satirlar = [
    ['Oturum', nf(seg.sessions), `toplamın ${pct(seg.sessions / (t.sessions || 1), 0)}`],
    ['Sepete ekleme', pct(seg.atcRate), `site ${pct(t.atcRate)}`],
    ['Satın alma', pct(seg.purchaseRate, 2), `site ${pct(t.purchaseRate, 2)}`],
    ...(t.revenue ? [['Ciro', tlShort(seg.revenue), `toplamın ${pct(seg.revenue / t.revenue, 0)}`]] : []),
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, background: C.line, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
      {satirlar.map(([e, d, a]) => (
        <div key={e} style={{ background: '#FAFBFF', padding: '10px 12px' }}>
          <div style={{ fontSize: 11.5, color: C.muted }}>{e}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{d}</div>
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 1 }}>{a}</div>
        </div>
      ))}
    </div>
  );
}

function BulguKarti({ b, veri, onNiyet, buyuk }) {
  const k = KIND_STYLE[b.kind] || KIND_STYLE.tespit;
  const o = bulguOlcusu(b, veri);
  const tik = !!b.intentKey;
  const kat = o && o.taban > 0 ? o.deger / o.taban : null;
  return (
    <div onClick={tik ? () => onNiyet(b.intentKey) : undefined} className={tik ? 'oc-kart oc-tik' : 'oc-kart'}
      style={{ gridRow: buyuk ? 'span 2' : undefined, border: `1px solid ${C.line}`, borderRadius: 12, padding: buyuk ? '20px 22px' : '16px 18px', background: C.card,
        cursor: tik ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', gap: buyuk ? 14 : 10, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 8, background: k.color, flex: 'none' }} />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: k.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</span>
        {o && <span style={{ fontSize: 12, color: C.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {o.ad}</span>}
      </div>
      <div style={{ fontSize: buyuk ? 19 : 15, fontWeight: 700, color: C.ink, letterSpacing: -0.2, lineHeight: 1.3 }}>{b.title}</div>
      {o && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: buyuk ? 44 : 28, fontWeight: 700, color: C.ink, letterSpacing: -1, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{pct(o.deger)}</span>
          <span style={{ fontSize: 12.5, color: C.muted }}>
            {o.etiket}{kat != null && <> · <b style={{ color: k.color }}>{o.tabanEtiket === 'oturum payı' ? 'oturum payının' : 'ortalamanın'} {nf(kat, kat < 10 ? 1 : 0)} katı</b></>}
          </span>
        </div>
      )}
      {o && <KiyasCubugu o={o} renk={k.color} h={buyuk ? 10 : 6} />}
      <div style={{ fontSize: buyuk ? 14 : 13, color: C.ink2, lineHeight: 1.55 }}>{b.body}</div>
      {buyuk && o && <SegmentOzeti seg={o.seg} t={veri.totals} />}
      {tik && <div style={{ marginTop: 'auto', fontSize: 12.5, color: C.accent, display: 'inline-flex', alignItems: 'center', gap: 2 }}>Niyet ayrıntısı <ChevronRight size={14} /></div>}
    </div>
  );
}

function OneCikanlar({ durum, veri, onNiyet }) {
  const { yukleniyor, hata, veri: iv } = durum;
  const bulgular = iv?.findings || [];
  if (!yukleniyor && !hata && !bulgular.length) return null;
  const sayac = ['risk', 'firsat', 'tespit'].map((k) => [k, bulgular.filter((b) => b.kind === k).length]).filter(([, n]) => n);
  return (
    <Bolum baslik="Öne çıkan bulgular" style={{ marginTop: 20 }}
      alt="Niyet, kanal, giriş ve ciro verisindeki en büyük sapmalar. Seçimi ve cümleleri yapay zeka yazar; büyük sayılar ve çubuklar doğrudan bu sayfanın verisinden gelir."
      sag={!yukleniyor && bulgular.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {sayac.map(([k, n]) => (
            <span key={k} style={{ fontSize: 12, fontWeight: 600, color: KIND_STYLE[k].color, background: KIND_STYLE[k].soft, padding: '3px 9px', borderRadius: 999 }}>{n} {KIND_STYLE[k].label.toLocaleLowerCase('tr-TR')}</span>
          ))}
          {iv?.generatedAt && <span style={{ fontSize: 12, color: C.faint, marginLeft: 4 }}>{new Date(iv.generatedAt).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
        </div>
      )}>
      <style>{`.oc-grid{display:grid;grid-template-columns:1.25fr 1fr 1fr;gap:12px}
.oc-tik{transition:border-color .15s, box-shadow .15s}.oc-tik:hover{border-color:${C.accent}!important;box-shadow:0 4px 14px rgba(67,106,235,.10)}
@keyframes ocNabiz{0%,100%{opacity:.55}50%{opacity:1}}
@media (max-width:1100px){.oc-grid{grid-template-columns:1fr 1fr}}
@media (max-width:680px){.oc-grid{grid-template-columns:1fr}.oc-grid>.oc-kart{grid-row:auto!important}}`}</style>
      {yukleniyor && (
        <div className="oc-grid">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ gridRow: i === 0 ? 'span 2' : undefined, minHeight: i === 0 ? 300 : 150, borderRadius: 12, background: C.hair, animation: `ocNabiz 1.4s ease-in-out ${i * 0.12}s infinite` }} />
          ))}
          <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: C.muted }}>Bulgular hazırlanıyor, ilk açılışta 15–20 sn sürebilir...</div>
        </div>
      )}
      {hata && !yukleniyor && <div style={{ fontSize: 13, color: C.warn }}>{hata}</div>}
      {!yukleniyor && bulgular.length > 0 && (
        <div className="oc-grid">
          {bulgular.map((b, i) => <BulguKarti key={i} b={b} veri={veri} onNiyet={onNiyet} buyuk={i === 0} />)}
        </div>
      )}
    </Bolum>
  );
}

/** Yatay çubuk satırı: etiket, çubuk, değer metni. */
function HBar({ etiket, alt, deger, maks, metin, renk = C.accent }) {
  const w = maks > 0 ? Math.max(1, Math.min(100, (deger / maks) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1.1fr) 2fr auto', gap: 10, alignItems: 'center', padding: '5px 0' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={etiket}>{etiket}</div>
        {alt && <div style={{ fontSize: 11, color: C.faint }}>{alt}</div>}
      </div>
      <div style={{ background: C.hair, borderRadius: 6, height: 10 }}>
        <div style={{ width: `${w}%`, height: 10, borderRadius: 6, background: renk }} />
      </div>
      <div style={{ fontSize: 12.5, color: C.ink, fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', minWidth: 90 }}>{metin}</div>
    </div>
  );
}

function BasisBar({ basis, yukseklik = 6 }) {
  const keys = ['search_term', 'campaign', 'gsc_estimate', 'landing'];
  const toplam = keys.reduce((a, k) => a + (basis?.[k] || 0), 0) || 1;
  return (
    <div style={{ display: 'flex', height: yukseklik, borderRadius: 4, overflow: 'hidden', background: C.hair }}
      title={keys.map((k) => `${BASIS_LABEL[k]}: ${pct((basis?.[k] || 0) / toplam, 0)}`).join(' · ')}>
      {keys.map((k) => <div key={k} style={{ width: `${((basis?.[k] || 0) / toplam) * 100}%`, background: BASIS_COLOR[k] }} />)}
    </div>
  );
}

function BasisLegend() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: C.muted }}>
      {Object.keys(BASIS_LABEL).map((k) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <i style={{ width: 9, height: 9, borderRadius: 3, background: BASIS_COLOR[k], display: 'inline-block' }} />{BASIS_LABEL[k]}
        </span>
      ))}
    </div>
  );
}

function Yukleniyor({ sn, gun }) {
  return (
    <div style={{ ...kart, padding: 40, marginTop: 20, textAlign: 'center' }}>
      <RefreshCw size={26} color={C.accent} style={{ animation: 'dsSpin 1s linear infinite' }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 14 }}>GA4&apos;ten hesaplanıyor, ilk açılış 20 sn kadar sürebilir</div>
      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 6 }}>
        Son {gun} gün için oturumlar niyet, giriş sayfası ve sonraki adımlara göre kümeleniyor.
        {gun === 90 && ' 90 günlük hesap daha uzun sürer.'} Geçen süre: {sn} sn. Sonuç önbelleğe alınır, sonraki açılışlar hızlıdır.
      </div>
      <style>{'@keyframes dsSpin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

/* ------------------------------ Niyet kartı ------------------------------ */
function NiyetKarti({ it, secili, onSec, satin }) {
  const top = it.topLandings?.[0];
  return (
    <button onClick={onSec} style={{
      ...kart, padding: 14, textAlign: 'left', cursor: 'pointer', fontFamily: FONT,
      borderColor: secili ? C.accent : C.line, boxShadow: secili ? `0 0 0 2px ${C.accentSoft}, 0 1px 2px rgba(16,24,40,.04)` : kart.boxShadow,
      background: secili ? '#FBFCFF' : C.card
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: C.ink, lineHeight: 1.25, minHeight: 34 }}>{it.label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: C.ink, letterSpacing: -0.4 }}>{nf(it.sessions)}</span>
        <span style={{ fontSize: 12, color: C.muted }}>oturum · {pct(it.share)}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: satin ? '1fr 1fr 1fr' : '1fr 1fr', gap: 6, marginTop: 10 }}>
        {[['Sepete ekleme', pct(it.atcRate)], ...(satin ? [['Satın alma', pct(it.purchaseRate, 2)]] : []), ['Etkileşimsiz çıkış', pct(it.bounceRate, 0)]].map(([l, v]) => (
          <div key={l}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink2 }}>{v}</div>
            <div style={{ fontSize: 10.5, color: C.faint, lineHeight: 1.2 }}>{l}</div>
          </div>
        ))}
      </div>
      {top && (
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={top.label}>
          En çok giriş: <b style={{ color: C.ink2, fontWeight: 600 }}>{top.label}</b> ({pct(top.share, 0)})
        </div>
      )}
      <div style={{ marginTop: 10 }}><BasisBar basis={it.basis} /></div>
    </button>
  );
}

/* ------------------------------ Niyet detayı ------------------------------ */
function SonrakiTipler({ list, bos }) {
  if (!list?.length) return <div style={{ fontSize: 12.5, color: C.muted }}>{bos}</div>;
  const maks = Math.max(...list.map((x) => x.per100));
  return list.slice(0, 8).map((x) => (
    <HBar key={x.type} etiket={x.label} deger={x.per100} maks={maks} metin={`100 oturumda ${nf(x.per100, 1)}`} renk={C.green} />
  ));
}

function NiyetDetay({ it, satin }) {
  const bosAdim = 'Bu niyet için adım verisi yok (yalnız en çok girilen 60 giriş sayfası analiz edilir).';
  const landMaks = Math.max(...(it.byLandingType || []).map((x) => x.sessions), 1);
  const chMaks = Math.max(...(it.byChannel || []).map((x) => x.sessions), 1);
  const sig = it.signals || {};
  const alt = { fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, margin: '0 0 8px' };
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
        <div style={{ ...kart, padding: 16, boxShadow: 'none' }}>
          <h3 style={alt}>1. Nereye girdi</h3>
          {(it.byLandingType || []).map((x) => (
            <HBar key={x.type} etiket={x.label} alt={`Sepete ekleme ${pct(x.atcRate)}`} deger={x.sessions} maks={landMaks} metin={`${nf(x.sessions)} · ${pct(x.share, 0)}`} />
          ))}
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={thL}>En çok girilen sayfalar</th><th style={th}>Oturum</th><th style={th}>Sepete ekl.</th>
                {satin && <th style={th}>Satın alma</th>}<th style={th}>Çıkış</th>
              </tr></thead>
              <tbody>
                {(it.topLandings || []).map((p) => (
                  <tr key={p.path}>
                    <td style={{ ...tdL, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.path}>{p.label}<span style={{ color: C.faint, fontSize: 11 }}> · {p.type}</span></td>
                    <td style={td}>{nf(p.sessions)}</td><td style={td}>{pct(p.atcRate)}</td>
                    {satin && <td style={td}>{pct(p.purchaseRate, 2)}</td>}<td style={td}>{pct(p.bounceRate, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ ...kart, padding: 16, boxShadow: 'none' }}>
          <h3 style={alt}>2. Sonra ne yaptı</h3>
          <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 6 }}>
            İlk adım türleri, bu niyetin her 100 oturumunda o türe geçiş sayısı (100&apos;ü aşabilir).
            Adım kapsamı: {pct(it.stepCoverage, 0)} oturum.
          </div>
          <SonrakiTipler list={it.nextStepTypes} bos={bosAdim} />
          {it.topNextSteps?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 6 }}>En çok gidilen sayfalar ve oradan sonrası</div>
              {it.topNextSteps.slice(0, 6).map((s) => (
                <div key={s.path} style={{ padding: '6px 0', borderBottom: `1px solid ${C.hair}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: C.ink }}>
                    <span title={s.path} style={{ fontWeight: 600 }}>{s.label} <span style={{ color: C.faint, fontWeight: 400, fontSize: 11 }}>{s.type}</span></span>
                    <span style={{ whiteSpace: 'nowrap', color: C.ink2 }}>100 oturumda {nf(s.per100, 1)}</span>
                  </div>
                  {s.then?.length > 0 && (
                    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                      <ChevronRight size={12} />
                      {s.then.map((t, i) => (
                        <span key={t.path}>{i > 0 && ' · '}{t.label} ({nf(t.per100, 1)})</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: C.faint, marginTop: 12 }}>Adımlar oturum içi sıralı yol değil; giriş sayfasından çıkan sayfa geçişlerinin toplamıdır (GA4 pageReferrer).</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, marginTop: 16 }}>
        <div style={{ ...kart, padding: 16, boxShadow: 'none' }}>
          <h3 style={alt}>3. Hangi kanaldan</h3>
          {(it.byChannel || []).map((x) => (
            <HBar key={x.channel} etiket={x.label} alt={`Sepete ekleme ${pct(x.atcRate)}${satin ? ` · satın alma ${pct(x.purchaseRate, 2)}` : ''}`}
              deger={x.sessions} maks={chMaks} metin={`${nf(x.sessions)} · ${pct(x.share, 0)}`} renk={C.purple} />
          ))}
        </div>
        <div style={{ ...kart, padding: 16, boxShadow: 'none' }}>
          <h3 style={alt}>4. Sinyaller: niyet nereden anlaşıldı</h3>
          <div style={{ marginBottom: 10 }}><BasisBar basis={it.basis} yukseklik={8} /></div>
          <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10 }}>
            {Object.keys(BASIS_LABEL).filter((k) => (it.basis?.[k] || 0) > 0.005).map((k) => `${BASIS_LABEL[k]} ${pct(it.basis[k], 0)}`).join(' · ')}
          </div>
          {sig.adsKeywords?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 4 }}>Google Ads arama terimleri</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {sig.adsKeywords.slice(0, 8).map((k) => (
                      <tr key={k.term}>
                        <td style={tdL}>{k.term}<span style={{ color: C.faint, fontSize: 11 }}> · {KIND_LABEL[k.kind] || k.kind}</span></td>
                        <td style={td}>{nf(k.sessions)}</td><td style={td}>{pct(k.atcRate)}</td>
                        {satin && <td style={td}>{pct(k.purchaseRate, 2)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {sig.gscQueries?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, margin: '10px 0 4px' }}>Search Console sorguları<Etiket>tahmini</Etiket></div>
              <div style={{ fontSize: 12.5, color: C.ink2, lineHeight: 1.7 }}>
                {sig.gscQueries.slice(0, 8).map((q) => (
                  <span key={q.query} style={{ display: 'inline-block', background: C.hair, borderRadius: 6, padding: '1px 8px', margin: '0 6px 4px 0' }}>{q.query} <span style={{ color: C.faint }}>{nf(q.clicks)}</span></span>
                ))}
              </div>
            </>
          )}
          {sig.campaigns?.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, margin: '10px 0 4px' }}>Kampanyalar</div>
              {sig.campaigns.slice(0, 6).map((c) => (
                <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, color: C.ink2, padding: '2px 0' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>{c.name}</span>
                  <span style={{ color: C.muted, whiteSpace: 'nowrap' }}>{nf(c.sessions)}</span>
                </div>
              ))}
            </>
          )}
          {!sig.adsKeywords?.length && !sig.gscQueries?.length && !sig.campaigns?.length && (
            <div style={{ fontSize: 12.5, color: C.muted }}>Bu niyet için ayrı bir terim veya kampanya sinyali yok; niyet giriş sayfasından çıkarıldı.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Karşılaştırma ------------------------------ */
const per100Of = (it, types) => (it.nextStepTypes || []).filter((x) => types.includes(x.type)).reduce((a, x) => a + x.per100, 0);

function Karsilastirma({ intents, a, b, setA, setB, satin }) {
  const A = intents.find((i) => i.key === a);
  const B = intents.find((i) => i.key === b);
  const sec = (v, set) => (
    <select value={v} onChange={(e) => set(e.target.value)} style={{ fontFamily: FONT, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.card, color: C.ink }}>
      {intents.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
    </select>
  );
  const satirlar = A && B ? [
    ['Oturum', (i) => nf(i.sessions)],
    ['Sepete ekleme oranı', (i) => pct(i.atcRate)],
    ...(satin ? [['Satın alma oranı', (i) => pct(i.purchaseRate, 2)], ['Ciro', (i) => tlShort(i.revenue)]] : []),
    ['Etkileşimsiz çıkış', (i) => pct(i.bounceRate, 0)],
    ['100 oturumda kategoriye geçiş', (i) => (i.nextStepTypes?.length ? nf(per100Of(i, ['category']), 1) : 'veri yok')],
    ['100 oturumda ürüne geçiş', (i) => (i.nextStepTypes?.length ? nf(per100Of(i, ['product']), 1) : 'veri yok')],
    ['100 oturumda sepete geçiş', (i) => (i.nextStepTypes?.length ? nf(per100Of(i, ['cart']), 1) : 'veri yok')],
    ['Ağırlıklı giriş türü', (i) => { const t = [...(i.byLandingType || [])].sort((x, y) => y.sessions - x.sessions)[0]; return t ? `${t.label} (${pct(t.share, 0)})` : '-'; }],
    ['En çok giriş sayfası', (i) => i.topLandings?.[0]?.label || '-'],
  ] : [];
  return (
    <Bolum baslik="Niyet karşılaştırma" alt="İki niyetin aynı ölçülerle yan yana görünümü. Örnek: yatak arayanlar ile bebek ve çocuk odası arayanlar."
      sag={<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{sec(a, setA)}<span style={{ color: C.faint, fontSize: 12 }}>ile</span>{sec(b, setB)}</div>}>
      {A && B ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thL}>Ölçü</th><th style={th}>{A.label}</th><th style={th}>{B.label}</th></tr></thead>
            <tbody>
              {satirlar.map(([l, f]) => (
                <tr key={l}><td style={tdL}>{l}</td><td style={{ ...td, fontWeight: 600, color: C.ink }}>{f(A)}</td><td style={{ ...td, fontWeight: 600, color: C.ink }}>{f(B)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div style={{ color: C.muted, fontSize: 13 }}>Karşılaştırılacak niyet bulunamadı.</div>}
    </Bolum>
  );
}

/* ------------------------------ Küme tablosu ------------------------------ */
const SIRALAR = [['sessions', 'Oturum'], ['atcRate', 'Sepete ekleme'], ['purchaseRate', 'Satın alma'], ['bounceRate', 'Çıkış'], ['revenue', 'Ciro']];

function KumeTablosu({ clusters, intents, satin }) {
  const [fi, setFi] = useState('');
  const [fk, setFk] = useState('');
  const [ft, setFt] = useState('');
  const [sirala, setSirala] = useState('sessions');
  const [yon, setYon] = useState(-1);
  const [acik, setAcik] = useState(null);
  const [limit, setLimit] = useState(40);

  const kanallar = useMemo(() => [...new Map(clusters.map((c) => [c.channel, c.channelLabel])).entries()], [clusters]);
  const tipler = useMemo(() => [...new Map(clusters.map((c) => [c.landingType, c.landingTypeLabel])).entries()], [clusters]);
  const liste = useMemo(() => {
    const l = clusters.filter((c) => (!fi || c.intent === fi) && (!fk || c.channel === fk) && (!ft || c.landingType === ft));
    return l.sort((x, y) => yon * ((x[sirala] || 0) - (y[sirala] || 0)));
  }, [clusters, fi, fk, ft, sirala, yon]);
  useEffect(() => { setLimit(40); }, [fi, fk, ft, sirala, yon]);

  const baslik = (k, label, left) => (
    <th style={{ ...(left ? thL : th), cursor: SIRALAR.some(([s]) => s === k) ? 'pointer' : 'default', color: sirala === k ? C.accent : C.muted }}
      onClick={() => { if (!SIRALAR.some(([s]) => s === k)) return; if (sirala === k) setYon(-yon); else { setSirala(k); setYon(-1); } }}>
      {label}{sirala === k ? (yon < 0 ? ' ↓' : ' ↑') : ''}
    </th>
  );
  const selS = { fontFamily: FONT, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.card, color: C.ink, maxWidth: 220 };
  const kolon = satin ? 9 : 7;

  return (
    <Bolum baslik="Küme tablosu: niyet, kanal ve giriş sayfası"
      alt={`Oturuma göre sıralanmış ilk ${nf(clusters.length)} küme. Satıra tıklayınca sonraki adımlar, örnek terimler ve kampanyalar açılır.`}
      sag={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={fi} onChange={(e) => setFi(e.target.value)} style={selS}><option value="">Tüm niyetler</option>{intents.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}</select>
          <select value={fk} onChange={(e) => setFk(e.target.value)} style={selS}><option value="">Tüm kanallar</option>{kanallar.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          <select value={ft} onChange={(e) => setFt(e.target.value)} style={selS}><option value="">Tüm giriş türleri</option>{tipler.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
      }>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...thL, width: 24 }} />
            {baslik('intentLabel', 'Niyet', true)}{baslik('channelLabel', 'Kanal', true)}{baslik('landingLabel', 'Giriş sayfası', true)}
            {baslik('sessions', 'Oturum')}{baslik('atcRate', 'Sepete ekleme')}
            {satin && baslik('purchaseRate', 'Satın alma')}{satin && baslik('revenue', 'Ciro')}{baslik('bounceRate', 'Etkileşimsiz çıkış')}
          </tr></thead>
          <tbody>
            {liste.slice(0, limit).map((c) => {
              const a = acik === c.id;
              return (
                <React.Fragment key={c.id}>
                  <tr onClick={() => setAcik(a ? null : c.id)} style={{ cursor: 'pointer', background: a ? '#FBFCFF' : 'transparent' }}>
                    <td style={tdL}>{a ? <ChevronDown size={14} color={C.muted} /> : <ChevronRight size={14} color={C.muted} />}</td>
                    <td style={{ ...tdL, color: C.ink }}>{c.intentLabel}{c.estimated && <Etiket>tahmini</Etiket>}</td>
                    <td style={tdL}>{c.channelLabel}</td>
                    <td style={{ ...tdL, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.landingPath}>{c.landingLabel}<span style={{ color: C.faint, fontSize: 11 }}> · {c.landingTypeLabel}</span></td>
                    <td style={td}>{nf(c.sessions)}</td><td style={td}>{pct(c.atcRate)}</td>
                    {satin && <td style={td}>{pct(c.purchaseRate, 2)}</td>}{satin && <td style={td}>{tlShort(c.revenue)}</td>}
                    <td style={td}>{pct(c.bounceRate, 0)}</td>
                  </tr>
                  {a && (
                    <tr><td colSpan={kolon} style={{ padding: '12px 16px 16px 40px', background: '#FBFCFF', borderBottom: `1px solid ${C.line}` }}>
                      <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8 }}>
                        Niyet kaynağı: {BASIS_LABEL[c.intentBasis] || c.intentBasis}{c.estimated && ' (tahmini pay, mutlak sayılar şişkin okunmalı)'}
                      </div>
                      {c.stepsAvailable ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 4 }}>Sonra ne yaptı (100 oturumda)</div>
                            <SonrakiTipler list={c.nextStepTypes} bos="Bu kümede giriş sonrası geçiş görülmedi." />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 4 }}>En çok gidilen sayfalar</div>
                            {(c.nextSteps || []).slice(0, 5).map((s) => (
                              <div key={s.path} style={{ fontSize: 12.5, color: C.ink2, padding: '3px 0' }}>
                                <b style={{ color: C.ink }}>{s.label}</b> <span style={{ color: C.muted }}>100 oturumda {nf(s.per100, 1)}</span>
                                {s.then?.length > 0 && <div style={{ fontSize: 11.5, color: C.muted }}>sonra: {s.then.map((t) => t.label).join(' · ')}</div>}
                              </div>
                            ))}
                            {!c.nextSteps?.length && <div style={{ fontSize: 12.5, color: C.muted }}>Sayfa bazlı geçiş yok.</div>}
                          </div>
                          <div>
                            {c.exampleTerms?.length > 0 && <><div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 4 }}>Örnek terimler</div>
                              <div style={{ fontSize: 12.5, color: C.ink2, marginBottom: 8 }}>{c.exampleTerms.join(' · ')}</div></>}
                            {c.campaigns?.length > 0 && <><div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 4 }}>Kampanyalar</div>
                              {c.campaigns.map((k) => <div key={k} style={{ fontSize: 12.5, color: C.ink2 }}>{k}</div>)}</>}
                            {!c.exampleTerms?.length && !c.campaigns?.length && <div style={{ fontSize: 12.5, color: C.muted }}>Terim veya kampanya bilgisi yok.</div>}
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12.5, color: C.muted }}>
                          Adım verisi yok (uzun kuyruk): bu giriş sayfası en çok girilen 60 sayfa arasında olmadığı için sonraki adımlar hesaplanmadı.
                          {(c.exampleTerms?.length > 0 || c.campaigns?.length > 0) && <div style={{ marginTop: 6 }}>{[...(c.exampleTerms || []), ...(c.campaigns || [])].join(' · ')}</div>}
                        </div>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {liste.length === 0 && <tr><td colSpan={kolon} style={{ ...tdL, color: C.muted, padding: 24 }}>Bu filtrelere uyan küme yok.</td></tr>}
          </tbody>
        </table>
      </div>
      {liste.length > limit && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button onClick={() => setLimit(limit + 60)} style={{ border: `1px solid ${C.line}`, background: C.card, borderRadius: 8, padding: '7px 16px', fontFamily: FONT, fontSize: 13, cursor: 'pointer', color: C.ink2 }}>
            Daha fazla göster ({nf(liste.length - limit)} küme daha)
          </button>
        </div>
      )}
    </Bolum>
  );
}

/* ------------------- Satın alan oturumda kaç sayfa ------------------- */
/**
 * Backend: GET /api/behavior-segments/pages-to-purchase — GA4 funnel raporu, ilk hesap ~1 dk;
 * { pending: true } dönerse yoklanır. groups[].level: 0 tümü, 1 ana kategori, 2 alt kategori (parentKey).
 */
const PTP_BANTLAR = [
  { k: 'b1', etiket: '1–10 sayfa', lo: 1, hi: 10, renk: '#C9D5FB', yazi: C.ink },
  { k: 'b2', etiket: '11–20', lo: 11, hi: 20, renk: '#8EA6F3', yazi: C.ink },
  { k: 'b3', etiket: '21–40', lo: 21, hi: 40, renk: '#436AEB', yazi: '#fff' },
  { k: 'b4', etiket: '41+', lo: 41, hi: Infinity, renk: '#1E3A8A', yazi: '#fff' },
];
const bantPaylari = (g) => PTP_BANTLAR.map((b) => ({
  ...b, pay: g.n ? (g.buckets || []).filter((x) => x.lo >= b.lo && x.lo <= b.hi).reduce((a, x) => a + x.n, 0) / g.n : 0
}));

function DagilimCubugu({ g }) {
  const p = bantPaylari(g);
  return (
    <div style={{ display: 'flex', height: 20, borderRadius: 5, overflow: 'hidden', minWidth: 220, background: C.hair }}
      title={p.map((b) => `${b.etiket}: ${pct(b.pay, 0)}`).join(' · ')}>
      {p.map((b) => (
        <div key={b.k} style={{ width: `${b.pay * 100}%`, background: b.renk, color: b.yazi, fontSize: 10.5, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {b.pay >= 0.1 ? pct(b.pay, 0) : ''}
        </div>
      ))}
    </div>
  );
}

function OzetKart({ etiket, deger, alt }) {
  return (
    <div style={{ background: '#FAFBFF', border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>{etiket}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.ink, marginTop: 4, letterSpacing: -0.3 }}>{deger}</div>
      {alt && <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>{alt}</div>}
    </div>
  );
}

/** Şerit ve ayrıntı tablosu aynı veriyi kullanır; istek sayfada bir kez atılır. */
function useSayfaSayisi(brandId, days, aktif) {
  const [d, setD] = useState({ yukleniyor: true, veri: null, hata: null });
  useEffect(() => {
    if (!aktif) return undefined;
    if (!brandId) return undefined;
    let iptal = false;
    let zaman = null;
    let deneme = 0;
    setD({ yukleniyor: true, veri: null, hata: null });
    const cek = () => {
      fetch(`${API_BASE}/api/behavior-segments/pages-to-purchase?days=${days}`, {
        credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
      }).then(async (res) => {
        const j = await res.json().catch(() => ({}));
        if (iptal) return;
        if (!res.ok) { setD({ yukleniyor: false, veri: null, hata: j.error || 'Hesaplanamadı.' }); return; }
        if (j.pending && deneme++ < 30) { zaman = setTimeout(cek, 8000); return; }
        if (j.pending) { setD({ yukleniyor: false, veri: null, hata: 'Hesap uzun sürdü, birazdan sayfayı yenileyin.' }); return; }
        setD({ yukleniyor: false, veri: j, hata: null });
      }).catch(() => { if (!iptal) setD({ yukleniyor: false, veri: null, hata: 'Hesaplanamadı.' }); });
    };
    cek();
    return () => { iptal = true; clearTimeout(zaman); };
  }, [brandId, days, aktif]);
  return d;
}

/** Eski önbellek kayıtlarında level yok: 'all' tümü, diğerleri ana kategori. */
const ptpSeviye = (g) => g.level ?? (g.key === 'all' ? 0 : 1);
/** PTP_MERGE ile birleştirilen ana grup (ör. Mobilya) adı parantezli gelir; şeritte onun yerine alt kategorileri gösterilir. */
const birlesikGrup = (g) => / \(.+\)$/.test(g.label || '');

/**
 * Sade özet (22 Eki 2026, Yataş/Alev talebi): kategori başına tipik müşterinin gezdiği sayfa + müşteri sayısı.
 * Ayrıntı ve yöntem aşağıdaki "Satın alan oturumda gezilen sayfa sayısı" bölümünde.
 */
function SayfaSayisiSerit({ d, days }) {
  const gruplar = (d.veri?.groups || []).filter((g) => g.n > 0);
  if (d.hata || d.veri?.empty || (!d.yukleniyor && !gruplar.length)) return null;
  const tum = gruplar.find((g) => ptpSeviye(g) === 0);
  const kalemler = gruplar
    .flatMap((g) => {
      if (ptpSeviye(g) !== 1) return [];
      if (!birlesikGrup(g)) return [g];
      return gruplar.filter((x) => x.parentKey === g.key && x.n >= 200);
    })
    .filter((g) => g.n >= 200)
    .sort((a, b) => a.median - b.median);
  const maks = Math.max(...kalemler.map((g) => g.median), tum?.median || 0, 1);
  const ayrintiyaGit = () => document.getElementById('sayfa-sayisi')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Bolum baslik="Satın almadan önce gezilen sayfa sayısı"
      alt={`Tipik müşterinin satın aldığı ziyarette gezdiği sayfa sayısı, aldığı ürünün kategorisine göre. Son ${days} gün.`}
      sag={!d.yukleniyor && (
        <button onClick={ayrintiyaGit} style={{ border: 0, background: 'transparent', padding: 0, fontFamily: FONT, fontSize: 12.5, color: C.accent, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          Ayrıntı ve yöntem <ChevronDown size={14} />
        </button>
      )}>
      {d.yukleniyor ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.muted }}>
          <RefreshCw size={14} color={C.accent} style={{ animation: 'dsSpin 1s linear infinite' }} />
          GA4&apos;ten hesaplanıyor.
          <style>{'@keyframes dsSpin { to { transform: rotate(360deg); } }'}</style>
        </div>
      ) : kalemler.length ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10 }}>
          {tum && <SeritKarti etiket="Tüm satın almalar" g={tum} maks={maks} vurgu />}
          {kalemler.map((g) => <SeritKarti key={g.key} etiket={g.label} g={g} maks={maks} />)}
        </div>
      ) : (
        // Hiçbir kategori 200 alıcıya ulaşmıyorsa (ör. Enza 28 gün) yalnız toplam + açıklama.
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {tum && <div style={{ flex: '0 0 200px' }}><SeritKarti etiket="Tüm satın almalar" g={tum} maks={maks} vurgu /></div>}
          <div style={{ flex: '1 1 260px', fontSize: 13, color: C.muted, lineHeight: 1.55 }}>
            Bu dönemde kategori kırılımı için yeterli satış yok (her kategoride 200'den az alıcı).
            {days < 90 && ' Daha uzun bir dönem seçince kategoriler görünebilir.'} Kategorilerin tahmini değerleri aşağıdaki tabloda &quot;az veri&quot; işaretiyle yer alıyor.
          </div>
        </div>
      )}
    </Bolum>
  );
}

function SeritKarti({ etiket, g, maks, vurgu }) {
  return (
    <div style={{ background: vurgu ? C.accentSoft : '#FAFBFF', border: `1px solid ${vurgu ? '#D6DFFC' : C.line}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 12.5, color: vurgu ? C.accent : C.ink2, fontWeight: 600, lineHeight: 1.3, minHeight: 32 }}>{etiket}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 700, color: C.ink, letterSpacing: -0.5 }}>{nf(g.median, 0)}</span>
        <span style={{ fontSize: 13, color: C.muted }}>sayfa</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: C.hair, marginTop: 8, overflow: 'hidden' }}>
        <div style={{ width: `${(g.median / maks) * 100}%`, height: '100%', background: C.accent, borderRadius: 2 }} />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{nf(g.n)} müşteri</div>
    </div>
  );
}

function SayfaSayisi({ d }) {
  const [kapali, setKapali] = useState(() => new Set());

  const gruplar = (d.veri?.groups || []).filter((g) => g.n > 0);
  const tum = gruplar.find((g) => g.level === 0);
  const anaGuvenli = gruplar.filter((g) => g.level === 1 && g.n >= 200);
  const altGuvenli = gruplar.filter((g) => g.level === 2 && g.n >= 200);
  const enKisa = [...anaGuvenli].sort((a, b) => a.median - b.median)[0];
  const enUzun = [...anaGuvenli].sort((a, b) => b.median - a.median)[0];
  const altUclar = [...altGuvenli].sort((a, b) => a.median - b.median);
  const anaAdi = (g) => (gruplar.find((x) => x.key === g.parentKey)?.label || '').replace(/ \(.*\)$/, '');
  const cocuklar = (key) => gruplar.filter((g) => g.parentKey === key);
  const anaVarCocuklu = gruplar.some((g) => g.level === 2);
  const degistir = (key) => setKapali((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const hepsiKapali = gruplar.filter((g) => g.level === 1 && cocuklar(g.key).length).every((g) => kapali.has(g.key));

  const satir = (g) => {
    const az = g.n < 200;
    const ust = g.level === 0;
    const alt = g.level === 2;
    const cocukSayisi = g.level === 1 ? cocuklar(g.key).length : 0;
    return (
      <tr key={g.key} style={ust ? { background: '#FAFBFF' } : undefined}>
        <td style={{ ...tdL, whiteSpace: 'normal', minWidth: 200, paddingLeft: alt ? 36 : 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            {cocukSayisi > 0 && (
              <button onClick={() => degistir(g.key)} title={kapali.has(g.key) ? 'Alt kategorileri göster' : 'Alt kategorileri gizle'}
                style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', marginTop: 1, display: 'inline-flex' }}>
                {kapali.has(g.key) ? <ChevronRight size={15} color={C.muted} /> : <ChevronDown size={15} color={C.muted} />}
              </button>
            )}
            <div>
              <div style={{ fontWeight: ust || g.level === 1 ? 700 : 500, color: alt ? C.ink2 : C.ink, fontSize: alt ? 12.5 : 13 }}>{g.label}</div>
              <div style={{ fontSize: 11, color: C.faint }}>
                {g.level === 1 && `toplam cironun ${pct(g.revenueShare, 0)}'i${cocukSayisi ? ` · ${cocukSayisi} alt kategori` : ''}`}
                {alt && `${anaAdi(g) || ''} cirosunun ${pct(g.shareOfParent, 0)}'i`}
              </div>
            </div>
          </div>
        </td>
        <td style={{ ...td, fontSize: alt ? 15 : 18, fontWeight: 700, color: C.ink }}>{nf(g.median, 0)}{az && <Etiket>az veri</Etiket>}</td>
        <td style={td}>{nf(g.mean, 0)}</td>
        <td style={td}>{nf(g.p25, 0)} – {nf(g.p75, 0)}</td>
        <td style={{ ...tdL, padding: '9px 16px' }}><DagilimCubugu g={g} /></td>
        <td style={{ ...td, color: C.muted }}>{nf(g.n)}</td>
      </tr>
    );
  };

  return (
    <Bolum baslik="Satın alan oturumda gezilen sayfa sayısı" style={{ scrollMarginTop: 16 }} id="sayfa-sayisi"
      alt="Satın almanın gerçekleştiği ziyarette kaç sayfa görüntülendi? Satın alınan ürünün kategorisine ve alt kategorisine göre."
      sag={anaVarCocuklu && (
        <button onClick={() => setKapali(hepsiKapali ? new Set() : new Set(gruplar.filter((g) => g.level === 1).map((g) => g.key)))}
          style={{ border: `1px solid ${C.line}`, background: C.card, borderRadius: 8, padding: '6px 12px', fontFamily: FONT, fontSize: 12.5, color: C.ink2, cursor: 'pointer' }}>
          {hepsiKapali ? 'Alt kategorileri göster' : 'Alt kategorileri gizle'}
        </button>
      )}>
      {d.yukleniyor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.muted, padding: '8px 0' }}>
          <RefreshCw size={14} color={C.accent} style={{ animation: 'dsSpin 1s linear infinite' }} />
          GA4&apos;ten hesaplanıyor, ilk hesap 1–2 dakika sürebilir.
          <style>{'@keyframes dsSpin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}
      {d.hata && <div style={{ fontSize: 13, color: C.warn }}>{d.hata}</div>}
      {d.veri?.empty && <div style={{ fontSize: 13, color: C.muted }}>{d.veri.reason || 'Bu dönem için satın alma verisi yok.'}</div>}
      {d.veri && !d.veri.empty && !gruplar.length && <div style={{ fontSize: 13, color: C.muted }}>Bu dönemde GA4&apos;te satın alma bulunamadı.</div>}
      {gruplar.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            {tum && (
              <OzetKart etiket="Site geneli" deger={`${nf(tum.median, 0)} sayfa`}
                alt={`Satın alanların yarısı ${nf(tum.median, 0)} sayfadan azını gezerek alıyor; %${nf(bantPaylari(tum)[0].pay * 100, 0)}'i 10 sayfa veya daha azında.`} />
            )}
            {enKisa && enUzun && enKisa.key !== enUzun.key && (
              <>
                <OzetKart etiket="En kısa yoldan alınan" deger={`${enKisa.label.replace(/ \(.*\)$/, '')} · ${nf(enKisa.median, 0)}`}
                  alt={`Site genelinden ${nf(Math.abs((tum?.median || 0) - enKisa.median), 0)} sayfa ${enKisa.median <= (tum?.median || 0) ? 'az' : 'fazla'}.`} />
                <OzetKart etiket="En çok gezilerek alınan" deger={`${enUzun.label.replace(/ \(.*\)$/, '')} · ${nf(enUzun.median, 0)}`}
                  alt={`Site genelinden ${nf(Math.abs(enUzun.median - (tum?.median || 0)), 0)} sayfa ${enUzun.median >= (tum?.median || 0) ? 'fazla' : 'az'}.`} />
              </>
            )}
            {altUclar.length >= 2 && (
              <OzetKart etiket="Alt kategorilerde iki uç" deger={`${nf(altUclar[0].median, 0)} – ${nf(altUclar[altUclar.length - 1].median, 0)} sayfa`}
                alt={`${altUclar[0].label} en kısa, ${altUclar[altUclar.length - 1].label} en uzun.`} />
            )}
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: C.muted, background: '#FAFBFF', border: `1px solid ${C.hair}`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, lineHeight: 1.5 }}>
            <span><b style={{ color: C.ink2 }}>Medyan:</b> alıcıların yarısı bundan az, yarısı bundan çok sayfa gezdi.</span>
            <span><b style={{ color: C.ink2 }}>Alıcıların yarısı:</b> en az ve en çok gezen çeyrekler çıkarıldığında kalan aralık.</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <b style={{ color: C.ink2 }}>Dağılım:</b>
              {PTP_BANTLAR.map((b) => (
                <span key={b.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <i style={{ width: 10, height: 10, borderRadius: 3, background: b.renk, display: 'inline-block' }} />{b.etiket}
                </span>
              ))}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <thead>
                <tr>
                  <th style={thL}>Ürün kategorisi</th>
                  <th style={th}>Medyan sayfa</th>
                  <th style={th}>Ortalama</th>
                  <th style={th}>Alıcıların yarısı</th>
                  <th style={thL}>Alıcıların sayfa sayısına göre dağılımı</th>
                  <th style={th}>Alıcı</th>
                </tr>
              </thead>
              <tbody>
                {gruplar.filter((g) => g.level !== 2).map((g) => [
                  satir(g),
                  ...(g.level === 1 && !kapali.has(g.key) ? cocuklar(g.key).map(satir) : []),
                ])}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.55 }}>
            Sayıya satın almanın olduğu ziyaretteki tüm sayfa görüntülemeleri girer: ödeme onay sayfası ve filtre/sıralama değişiminde yeniden yüklenen sayfalar dahil, aynı kişinin önceki ziyaretleri hariç.
            Medyan, ortalamadan daha temsilidir; ortalamayı çok uzun gezen az sayıda kişi yukarı çeker.
            GA4 bu raporu örnekleyerek hesaplar; alıcı sayısı 200&apos;ün altındaki satırlarda (az veri) rakam kaba bir tahmindir.
            Bir siparişte birden fazla kategori varsa sipariş her kategoride sayılır. Alt kategoriler GA4 ürün kategorisi hiyerarşisinden gelir.
            {d.veri.meta?.generatedAt && ` Hesaplanma: ${new Date(d.veri.meta.generatedAt).toLocaleString('tr-TR')}.`}
          </div>
        </>
      )}
    </Bolum>
  );
}

/* ------------------------------ Sayfa ------------------------------ */
export default function YatasDavranisSegmentasyonu() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const [days, setDays] = useState(28);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [sn, setSn] = useState(0);
  const [secili, setSecili] = useState(null);
  const [cmpA, setCmpA] = useState('mattress');
  const [cmpB, setCmpB] = useState('baby_kids');

  const yukle = useCallback(async (refresh = false) => {
    if (!brandId) return;
    setYukleniyor(true); setHata(null); setSn(0);
    try {
      const res = await fetch(`${API_BASE}/api/behavior-segments?days=${days}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        const e = new Error(j.error || j.message || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      setVeri(j);
    } catch (e) {
      setVeri(null);
      setHata({ status: e.status, message: e.message });
    } finally {
      setYukleniyor(false);
    }
  }, [brandId, days]);

  useEffect(() => { setVeri(null); setSecili(null); yukle(); }, [yukle]);
  useEffect(() => {
    if (!yukleniyor) return undefined;
    const t = setInterval(() => setSn((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [yukleniyor]);

  // Bulgular ana veri geldikten sonra ayrı istenir; ana tabloyu bekletmez.
  const [bulgu, setBulgu] = useState({ yukleniyor: false, hata: null, veri: null });
  const veriZamani = veri?.meta?.generatedAt;
  useEffect(() => {
    if (!brandId || !veriZamani || veri?.empty) { setBulgu({ yukleniyor: false, hata: null, veri: null }); return undefined; }
    let iptal = false;
    setBulgu({ yukleniyor: true, hata: null, veri: null });
    fetch(`${API_BASE}/api/behavior-segments/insights?days=${days}`, {
      credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
    }).then(async (res) => {
      const j = await res.json().catch(() => ({}));
      if (iptal) return;
      if (!res.ok) setBulgu({ yukleniyor: false, hata: j.error || 'Öne çıkan bulgular şu an üretilemedi.', veri: null });
      else setBulgu({ yukleniyor: false, hata: null, veri: j });
    }).catch(() => { if (!iptal) setBulgu({ yukleniyor: false, hata: 'Öne çıkan bulgular şu an üretilemedi.', veri: null }); });
    return () => { iptal = true; };
  }, [brandId, days, veriZamani, veri?.empty]);
  const niyeteGit = (key) => {
    setSecili(key);
    setTimeout(() => document.getElementById('niyet-detay')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const meta = veri?.meta;
  const satin = meta?.hasPurchaseData !== false;
  const intents = useMemo(() => [...(veri?.intents || [])].sort((a, b) => b.sessions - a.sessions), [veri]);
  useEffect(() => {
    if (!intents.length) return;
    setSecili((s) => (s && intents.some((i) => i.key === s) ? s : intents[0].key));
    setCmpA((a) => (intents.some((i) => i.key === a) ? a : intents[0].key));
    setCmpB((b) => (intents.some((i) => i.key === b) ? b : (intents[1] || intents[0]).key));
  }, [intents]);
  const seciliIntent = intents.find((i) => i.key === secili);

  const bos = veri?.empty || (veri && !intents.length);
  const ptp = useSayfaSayisi(brandId, days, Boolean(brandId && veri && !bos && satin));

  return (
    <div style={{ background: C.ground, minHeight: '100%', padding: '24px 28px 48px', fontFamily: FONT, color: C.ink }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>Davranış Segmentasyonu</h1>
            <div style={{ fontSize: 13.5, color: C.muted, marginTop: 4, maxWidth: 720 }}>
              {selectedBrand?.name ? `${selectedBrand.name} · ` : ''}Niyet, giriş ve davranış: insanlar ne için geldi, nereye girdi, sonra ne yaptı. Kaynaktan bağımsız kümeler.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {meta?.range && <span style={{ fontSize: 12.5, color: C.muted }}>{gunEtiket(meta.range.start)} – {gunEtiket(meta.range.end)}</span>}
            <Secici secenekler={[[7, '7 gün'], [28, '28 gün'], [90, '90 gün']]} deger={days} onChange={setDays} />
            <button onClick={() => yukle(true)} disabled={yukleniyor} title="Önbelleği atla, yeniden hesapla" style={{
              border: `1px solid ${C.line}`, background: C.card, borderRadius: 10, padding: '7px 10px', cursor: yukleniyor ? 'default' : 'pointer', opacity: yukleniyor ? 0.5 : 1, display: 'inline-flex', alignItems: 'center'
            }}><RefreshCw size={15} color={C.muted} /></button>
          </div>
        </div>

        {meta?.stale && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: C.accent, background: C.accentSoft, borderRadius: 8, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} /> Arkada yenileniyor; gördüğünüz veri {meta.ageSec != null ? `${nf(Math.round(meta.ageSec / 60))} dk` : 'bir süre'} önce hesaplandı.
          </div>
        )}

        {!brandId && <div style={{ ...kart, padding: 24, marginTop: 20, color: C.muted }}>Önce bir marka seçin.</div>}

        {yukleniyor && !veri && <Yukleniyor sn={sn} gun={days} />}

        {hata && !yukleniyor && (
          <div style={{ ...kart, padding: 24, marginTop: 20, borderColor: '#FED7AA', background: C.warnSoft }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: C.warn, fontWeight: 600 }}>
              <AlertTriangle size={18} />
              {hata.status === 403 ? 'Bu sayfaya erişim yetkiniz yok' : hata.status === 400 ? 'Bu marka için davranış segmentasyonu kurulu değil' : 'Veri alınamadı'}
            </div>
            <div style={{ fontSize: 13, color: C.ink2, marginTop: 8 }}>
              {hata.status === 403 && 'Sayfa yalnız Yataş grubu markaları için açıktır.'}
              {hata.status === 400 && 'Marka için GA4 mülkü bağlı olmayabilir. '}
              {hata.message}
            </div>
            {hata.status !== 403 && (
              <button onClick={() => yukle()} style={{ marginTop: 12, border: 0, background: C.accent, color: '#fff', borderRadius: 8, padding: '7px 16px', fontFamily: FONT, fontSize: 13, cursor: 'pointer' }}>Tekrar dene</button>
            )}
          </div>
        )}

        {bos && !yukleniyor && !hata && (
          <div style={{ ...kart, padding: 28, marginTop: 20, textAlign: 'center', color: C.muted }}>
            <Info size={22} color={C.faint} />
            <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginTop: 8 }}>Bu dönem için gösterilecek veri yok</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{veri?.reason || 'Seçili markada bu aralıkta oturum bulunamadı. Farklı bir aralık deneyin.'}</div>
          </div>
        )}

        {veri && !bos && (
          <>
            {yukleniyor && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 12 }}>Yeni aralık hesaplanıyor... {sn} sn</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginTop: 20 }}>
              <Kpi etiket="Toplam oturum" deger={nf(veri.totals.sessions)} alt={`${nf(veri.totals.intents)} niyet · ${nf(veri.totals.clusters)} küme`} />
              <Kpi etiket="Sepete ekleme oranı" deger={pct(veri.totals.atcRate)} alt={`${nf(veri.totals.atc)} sepete ekleme`} />
              {satin && <Kpi etiket="Satın alma oranı" deger={pct(veri.totals.purchaseRate, 2)} alt={`${nf(veri.totals.purchases)} sipariş`} />}
              {satin && <Kpi etiket="Ciro" deger={tlShort(veri.totals.revenue)} alt={tl(veri.totals.revenue)} />}
              <Kpi etiket="Etkileşimsiz çıkış" deger={pct(veri.totals.bounceRate, 0)} alt="Etkileşimli olmayan oturum payı" />
            </div>
            {!satin && (
              <div style={{ fontSize: 12.5, color: C.warn, background: C.warnSoft, borderRadius: 8, padding: '8px 12px', marginTop: 12 }}>
                Bu markada GA4 satın alma ve ciro verisi bulunmadığı için satın alma ve ciro sütunları gizlendi.
              </div>
            )}

            {satin && <SayfaSayisiSerit d={ptp} days={days} />}

            <OneCikanlar durum={bulgu} veri={veri} onNiyet={niyeteGit} />

            <Bolum baslik="Niyet kümeleri" alt="İnsanlar ne için geldi? Kart seçince aşağıda o niyetin giriş ve davranış ayrıntısı açılır. Alttaki renkli çubuk niyetin hangi sinyalden belirlendiğini gösterir."
              sag={<BasisLegend />}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
                {intents.map((it) => <NiyetKarti key={it.key} it={it} satin={satin} secili={it.key === secili} onSec={() => setSecili(it.key)} />)}
              </div>
            </Bolum>

            <div id="niyet-detay" style={{ scrollMarginTop: 16 }} />
            {seciliIntent && (
              <Bolum baslik={`Niyet ayrıntısı: ${seciliIntent.label}`}
                alt={`${nf(seciliIntent.sessions)} oturum (toplamın ${pct(seciliIntent.share, 0)}). ${seciliIntent.key === 'other' ? 'Sınıflanamayan oturumlar burada toplanır.' : ''}`}>
                <NiyetDetay it={seciliIntent} satin={satin} />
              </Bolum>
            )}

            <Karsilastirma intents={intents} a={cmpA} b={cmpB} setA={setCmpA} setB={setCmpB} satin={satin} />

            {satin && <SayfaSayisi d={ptp} />}

            <KumeTablosu clusters={veri.clusters || []} intents={intents} satin={satin} />

            <Bolum baslik="Veri kaynakları ve sınırlar" alt="Bu sayfadaki sayıları yorumlarken bilinmesi gerekenler.">
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Kaynaklar</div>
              {(meta.sources || []).map((s) => (
                <div key={s.id} style={{ fontSize: 13, color: s.used ? C.ink2 : C.faint, padding: '3px 0' }}>
                  {s.used ? 'Kullanıldı' : 'Kullanılmadı'} · {s.label}
                </div>
              ))}
              {meta.organicVsGsc && (
                <div style={{ fontSize: 13, color: C.ink2, marginTop: 12, background: C.warnSoft, borderRadius: 8, padding: '8px 12px' }}>
                  Organik arama: GA4 {nf(meta.organicVsGsc.ga4OrganicSessions)} oturum, Search Console {nf(meta.organicVsGsc.gscClicks)} tıklama
                  {meta.organicVsGsc.gscClicks > 0 && ` (GA4 ${nf(meta.organicVsGsc.ga4OrganicSessions / meta.organicVsGsc.gscClicks, 2)} kat)`}.
                  Organik niyet payları GSC dağılımından tahmin edildiği için organik kümelerin mutlak sayıları şişkin okunmalı; oranlar daha güvenilirdir.
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, margin: '14px 0 6px' }}>Sınırlar</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(meta.limits || []).map((l, i) => <li key={i} style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55, marginBottom: 4 }}>{l}</li>)}
              </ul>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 12 }}>
                Hesaplanma: {meta.generatedAt ? new Date(meta.generatedAt).toLocaleString('tr-TR') : '-'}{meta.cached ? ' (önbellekten)' : ''}
                {meta.computeMs ? ` · hesap süresi ${nf(meta.computeMs / 1000, 1)} sn` : ''}
                {meta.steps?.enabled ? ` · adım analizi ${nf(meta.steps.landingsCovered)} giriş sayfası, oturumların ${pct(meta.steps.sessionShareCovered, 0)}'i` : ''}
              </div>
            </Bolum>
          </>
        )}
      </div>
    </div>
  );
}
